import React, { useEffect, useRef } from "react";
import { Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import {
  PendingJob,
  removePendingJob,
} from "../../store/slices/misc/pendingJobsSlice";
import {
  setHighlight,
  setUploadHighlight,
  setExportStatus,
} from "../../store/slices/misc/highlightSlice";
import { normsApis } from "../../api/Norms";
import { manpowerApis } from "../../api/ManpowerNorms";
import { machineryApis } from "../../api/MachineryNorms";
import {
  equipmentRateApis,
  deploymentNormApis,
} from "../../api/MachineryEquipment";
import { uploadApis } from "../../api/Uploads";
import {
  parseReport,
  isCreationDone,
  isTerminalFailure,
} from "../upload/shared/uploadReport";

// Mounted ONCE in the always-present authenticated header (HeaderMain). Watches
// the pendingJobs slice — jobs whose modal was closed/timed-out before their
// backend export/import finished — and, when one reaches a terminal state, pops
// an antd notification (Download / View action) and lights the existing header
// avatar badge. Modals that stay open own their own result and remove the job
// themselves, so only orphaned jobs are notified here (no double-toast).

const POLL_INTERVAL = 4000;

// Plain styled anchor for the export download — NOT an antd Button (global CSS
// forces a blue background on themed buttons; same gotcha as NormsExportModal).
const downloadAnchorStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  color: "#1ee0ac",
  fontWeight: 500,
};

const BackgroundJobWatcher: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jobs = useSelector((state: RootState) => state.pendingJobs.jobs);

  // latest jobs, read inside the loop without re-arming the effect on every add
  const jobsRef = useRef<PendingJob[]>(jobs);
  jobsRef.current = jobs;

  // generation guard — bumping it kills any in-flight loop (mirrors the poll
  // pattern in useUploadReportPoll / NormsExportModal)
  const genRef = useRef(0);
  // jobs already notified this session — belt-and-suspenders against a second
  // toast before removePendingJob propagates through the store
  const notifiedRef = useRef<Set<string>>(new Set());

  const hasJobs = jobs.length > 0;

  useEffect(() => {
    if (!hasJobs) {
      genRef.current++; // ensure any lingering loop stops
      return;
    }
    const gen = ++genRef.current;

    const finalizeExportDone = (job: PendingJob, record: any) => {
      if (notifiedRef.current.has(job.jobId)) return;
      notifiedRef.current.add(job.jobId);
      notification.success({
        key: job.jobId,
        message: "Export ready",
        description: job.label,
        duration: 0,
        btn: (
          <a
            href={record.filePath}
            target="_blank"
            rel="noreferrer"
            download
            title={record?.fileName || "Download export"}
            style={downloadAnchorStyle}
            onClick={() => notification.destroy(job.jobId)}
          >
            Download
          </a>
        ),
      });
      dispatch(setHighlight(true));
      dispatch(setExportStatus("ready")); // green mark on History
      dispatch(removePendingJob(job.jobId));
    };

    const finalizeExportFailed = (job: PendingJob, record: any) => {
      if (notifiedRef.current.has(job.jobId)) return;
      notifiedRef.current.add(job.jobId);
      notification.error({
        key: job.jobId,
        message: "Export failed",
        description: record?.error
          ? `${job.label}: ${record.error}`
          : `${job.label} could not be generated.`,
        duration: 0,
      });
      dispatch(setHighlight(true));
      dispatch(setExportStatus("failed")); // red mark on History
      dispatch(removePendingJob(job.jobId));
    };

    const finalizeImportDone = (job: PendingJob, record: any) => {
      if (notifiedRef.current.has(job.jobId)) return;
      notifiedRef.current.add(job.jobId);
      notification.success({
        key: job.jobId,
        message: "Import finished",
        description: `${job.label}: ${record.successCount} created, ${record.failureCount} error(s)`,
        duration: 0,
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy(job.jobId);
              navigate(job.historyPath);
            }}
          >
            View
          </Button>
        ),
      });
      dispatch(setUploadHighlight(true));
      dispatch(removePendingJob(job.jobId));
    };

    const finalizeImportFailed = (job: PendingJob, record: any) => {
      if (notifiedRef.current.has(job.jobId)) return;
      notifiedRef.current.add(job.jobId);
      notification.error({
        key: job.jobId,
        message: "Import failed",
        description: record?.topError
          ? `${job.label}: ${record.topError}`
          : `${job.label} failed — nothing was saved.`,
        duration: 0,
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy(job.jobId);
              navigate(job.historyPath);
            }}
          >
            View
          </Button>
        ),
      });
      dispatch(setUploadHighlight(true));
      dispatch(removePendingJob(job.jobId));
    };

    const tick = async () => {
      const current = jobsRef.current;
      const exportJobs = current.filter((j) => j.kind === "export");
      const importJobs = current.filter((j) => j.kind === "import");

      // Exports: each export master (norms / manpower) owns its own request
      // list, so group the jobs by listKind and poll each list once. Jobs
      // persisted before manpower existed have no listKind → default "norms".
      if (exportJobs.length) {
        const exportListFetchers: Record<string, () => Promise<any>> = {
          norms: normsApis.fetchNormsExportList,
          manpower: manpowerApis.fetchExportList,
          machinery: machineryApis.fetchExportList,
          "equipment-rates": equipmentRateApis.fetchExportList,
          "deployment-norms": deploymentNormApis.fetchExportList,
        };
        const jobsByKind: Record<string, PendingJob[]> = {};
        for (const job of exportJobs) {
          const kind = job.listKind || "norms";
          (jobsByKind[kind] = jobsByKind[kind] || []).push(job);
        }
        for (const kind of Object.keys(jobsByKind)) {
          const kindJobs = jobsByKind[kind];
          const fetcher = exportListFetchers[kind] || normsApis.fetchNormsExportList;
          try {
            const res = await fetcher();
            if (genRef.current !== gen) return;
            const records: any[] = res?.data?.records || [];
            const byId = new Map<number, any>(
              records.map((r): [number, any] => [Number(r.reportId), r])
            );
            for (const job of kindJobs) {
              const rec = byId.get(job.reportId);
              if (!rec) continue;
              if (rec.status === "Processed" && rec.filePath) {
                finalizeExportDone(job, rec);
              } else if (rec.status === "Failed") {
                finalizeExportFailed(job, rec);
              }
            }
          } catch (err) {
            // transient — retry next tick
            console.error(
              `BackgroundJobWatcher ${kind} export poll failed:`,
              err
            );
          }
        }
      }

      // Imports: one report fetch per job (first page only — we just need status).
      for (const job of importJobs) {
        if (genRef.current !== gen) return;
        try {
          const res = await uploadApis.fetchWBSReportById(job.reportId, 1, 1);
          if (genRef.current !== gen) return;
          const record = parseReport(res?.data || res);
          if (isCreationDone(record)) {
            finalizeImportDone(job, record);
          } else if (isTerminalFailure(record)) {
            finalizeImportFailed(job, record);
          }
        } catch (err) {
          console.error("BackgroundJobWatcher import poll failed:", err);
        }
      }
    };

    let stopped = false;
    const loop = async () => {
      while (!stopped && genRef.current === gen) {
        if (jobsRef.current.length === 0) break; // all done — let effect re-arm
        await tick();
        if (stopped || genRef.current !== gen) break;
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }
    };
    loop();

    return () => {
      stopped = true;
      genRef.current++;
    };
  }, [hasJobs, dispatch, navigate]);

  return null;
};

export default BackgroundJobWatcher;
